import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listCustomers,
  listCatalog,
  createSubscription,
  updateSubscription,
  endSubscription,
  deleteSubscription,
  listFlagCodes,
  listChannelsByFlag,
  createOneTimeFee,
  listOneTimeFeeForMonth,
} from '../../api/system/system.api';
import { BASE_MONTH_KEY, monthOptions, thisMonth, useBaseMonth } from '../../libs/baseMonth';
import { MAX_BILLING_DATE, MIN_BILLING_DATE, assertBillingMonth, assertBillingStartAt } from '../../libs/billingDate';
import { END_MONTH_PROMPT, isBillableForMonth } from '../../libs/subscriptionPeriod';

// 전체 고객(A2) — 전 채널을 생성번호(channelId) 순으로 나열, 채널당 2줄.
//  1줄: channelId / 로고(account_channel.logo_url) / 채널명 / 계정주(accountId) /
//       serviceStatus / is_used / has_profile_key / has_rest_api_key / has_javascript_key
//  2줄: 구독 전체를 '구독 관리' 탭과 동일한 목록으로 표시 + 수정/종료/삭제 + 구독 추가
// initial data 확정용.

const won = (n) => (n ?? 0).toLocaleString('ko-KR');

// month 기준 과금 라인 여부 — 서버 billableIn 과 같은 규칙(ENDED 도 종료월까지는 포함). libs/subscriptionPeriod.js
const isActiveForMonth = isBillableForMonth;

const STATUS_BADGE = {
  ACTIVE: 'bg-green-100 text-green-800',
  ENDED: 'bg-gray-200 text-gray-500',
};

// 일회성 비용 상태(PENDING = 아직 정산서에 안 들어감 / BILLED = 정산서 확정과 함께 청구됨)
const FEE_STATUS = {
  PENDING: { label: '청구 대기', cls: 'bg-yellow-100 text-yellow-800' },
  BILLED: { label: '청구됨', cls: 'bg-green-100 text-green-800' },
};
const FEE_TYPE_LABEL = { INSTALL: '설치비', STICKER: '스티커', ETC: '기타' };

// on/off 배지
const Flag = ({ on, label }) => (
  <span
    className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${
      on ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'
    }`}
  >
    {on ? '✓' : '✕'} {label}
  </span>
);

// onOpenDetail: 채널 행의 '상세 →' 버튼 → SystemAdminPage 가 '고객사 상세' 탭으로 전환(A2 Phase E).
export default function AllCustomersAdmin({ onOpenDetail }) {
  // 기준월은 화면을 옮겨도 유지된다(고객사 상세와 같은 키를 공유) — libs/baseMonth.js 참고.
  const [month, setMonth] = useBaseMonth(BASE_MONTH_KEY, thisMonth());
  const months = useMemo(() => monthOptions(), []);
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // 필터
  const [keyword, setKeyword] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [missingOnly, setMissingOnly] = useState(false);
  const [feeOnly, setFeeOnly] = useState(false);

  // 기준월의 일회성 비용(전 채널) — 채널 id → 목록. 기준월을 바꾸면 다시 부른다(2026-10-01).
  // 종전 이 화면은 구독만 보여 줘서, 그 달 설치비·스티커가 걸린 채널인지 알려면 고객사 상세를 하나씩 열어야 했다.
  const [feesByChannel, setFeesByChannel] = useState(new Map());
  // null = 정상, 문자열 = 조회 실패 사유. 🔴 조용히 비우지 않는다 — '이 달 비용 없음' 으로 오해하면 청구 누락으로 이어진다.
  const [feeLoadErr, setFeeLoadErr] = useState(null);
  const loadMonthFees = useCallback(async (m) => {
    try {
      const res = await listOneTimeFeeForMonth(m);
      const map = new Map();
      (res?.data ?? []).forEach((f) => {
        if (!map.has(f.channelId)) map.set(f.channelId, []);
        map.get(f.channelId).push(f);
      });
      setFeesByChannel(map);
      setFeeLoadErr(null);
    } catch (e) {
      setFeesByChannel(new Map());
      setFeeLoadErr(e?.message || '조회 실패');
    }
  }, []);
  useEffect(() => { loadMonthFees(month); }, [month, loadMonthFees]);

  // 구독 추가 (열린 채널 + draft)
  const emptyDraft = () => ({
    itemType: '',
    effectiveFrom: month,
    branchCount: 1,
    unitPriceNet: '',
    description: '',
    billingStartAt: '',
  });
  const [addingFor, setAddingFor] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());

  const run = async (fn, okMsg) => {
    setLoading(true);
    setMsg('');
    try {
      await fn();
      if (okMsg) setMsg(okMsg);
    } catch (e) {
      setMsg(`오류: ${e?.response?.data?.data || e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const reload = async () => {
    const c = await listCustomers();
    setRows(c?.data ?? []);
  };

  const load = () => run(async () => {
    loadMonthFees(month);
    const [c, cat] = await Promise.all([listCustomers(), listCatalog(true)]);
    setRows(c?.data ?? []);
    // 계약형(MONTHLY/ACQUISITION) 카탈로그만 구독 대상
    setCatalog((cat?.data ?? []).filter((r) => r.category === 'MONTHLY' || r.category === 'ACQUISITION'));
  });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCatalog = catalog.find((r) => r.itemType === draft.itemType);

  // 일회성 비용 추가 (구독 추가와 같은 방식 — 독립 탭을 없애고 고객사 단위로 옮겼다, 2026-08-04)
  const FEE_DEFAULT = { INSTALL: { name: '설치비', amountNet: 50000 }, STICKER: { name: '스티커', amountNet: 30000 } };
  const emptyFee = () => ({ feeType: 'INSTALL', name: '설치비', amountNet: 50000, billingMonth: month, description: '' });
  const [feeAddingFor, setFeeAddingFor] = useState(null);
  const [feeDraft, setFeeDraft] = useState(emptyFee());

  const openFeeAdd = (channelId) => {
    setFeeAddingFor(channelId);
    setFeeDraft(emptyFee());
  };

  // 종류를 고르면 항목명·금액을 기본값으로 채운다(이후 수정 가능).
  const pickFeeType = (feeType) => {
    const d = FEE_DEFAULT[feeType];
    setFeeDraft((f) => ({ ...f, feeType, ...(d ? { name: d.name, amountNet: d.amountNet } : {}) }));
  };

  const doCreateFee = (channelId) => run(async () => {
    if (!feeDraft.name.trim()) throw new Error('항목명을 입력하세요.');
    if (!/^\d{4}-\d{2}$/.test(feeDraft.billingMonth)) throw new Error('청구월은 YYYY-MM 형식으로 입력하세요.');
    await createOneTimeFee({
      channelId,
      feeType: feeDraft.feeType,
      name: feeDraft.name,
      amountNet: Number(feeDraft.amountNet) || 0,
      billingMonth: feeDraft.billingMonth,
      description: feeDraft.description || null,
    });
    setFeeAddingFor(null);
    await loadMonthFees(month);
  }, '일회성 비용 추가 완료 — 해당 월 정산 생성 시 자동 반영됩니다.');


  const openAdd = (channelId) => {
    setAddingFor(channelId);
    setDraft(emptyDraft());
    setMsg('');
  };

  const doCreate = (channelId) => run(async () => {
    if (!draft.itemType) { setMsg('상품(itemType)을 선택하세요.'); return; }
    // 🔴 연도 오타를 여기서 막는다 — 서버(400)까지 가기 전에 그 자리에서 알려준다.
    //    2026-09-02 인시던트: 잘못된 연도가 0000-00-00 으로 저장돼 전체 고객 화면이 500 이 났다.
    assertBillingMonth(draft.effectiveFrom, '적용 시작월');
    assertBillingStartAt(draft.billingStartAt);
    await createSubscription({
      channelId: Number(channelId),
      itemType: draft.itemType,
      effectiveFrom: draft.effectiveFrom,
      branchCount: Number(draft.branchCount) || 1,
      unitPriceNet: draft.unitPriceNet === '' ? null : Number(draft.unitPriceNet),
      description: draft.description || null,
      billingStartAt: draft.billingStartAt || null,
    });
    setAddingFor(null);
    await reload();
  }, '구독 추가 완료');

  // 채널 r 의 구독 sub 필드 인라인 수정
  const setSubField = (channelId, subId, field, value) => {
    setRows((prev) => prev.map((r) => (
      r.channelId !== channelId ? r : {
        ...r,
        subscriptions: r.subscriptions.map((s) => (s.id === subId ? { ...s, [field]: value } : s)),
      }
    )));
  };

  const doSave = (s) => run(async () => {
    // 🔴 서버는 첫 유료일을 `2026-09-04T00:00+09:00`(KST offset) 로 내려준다. 날짜 칸은 앞 10자리만
    //    보여 주므로, 그 칸을 건드리지 않고 다른 칸(예: 종료월)만 고쳐 저장하면 원래 값이 그대로 남아
    //    `assertBillingStartAt`(YYYY-MM-DD 만 허용)에 걸렸다 — "첫 유료일 형식이 올바르지 않습니다"(2026-10-01).
    //    화면에 보이는 날짜(KST 앞 10자리)로 맞춰 검증·전송한다. 서버는 이를 KST 자정으로 해석한다.
    const billingStartAt = (s.billingStartAt || '').slice(0, 10);
    assertBillingMonth(s.effectiveFrom, '적용 시작월');
    assertBillingMonth(s.effectiveTo, '적용 종료월', false);
    assertBillingStartAt(billingStartAt);
    await updateSubscription(s.id, {
      branchCount: Number(s.branchCount) || 1,
      unitPriceNet: Number(s.unitPriceNet) || 0,
      name: s.name,
      description: s.description || null,
      billingStartAt: billingStartAt || null,
      effectiveFrom: s.effectiveFrom,
      effectiveTo: s.effectiveTo || null,
      status: s.status,
      sortOrder: Number(s.sortOrder) || 0,
    });
    await reload();
  }, `구독 #${s.id} 저장 완료`);

  const doEnd = (s) => run(async () => {
    const to = window.prompt(END_MONTH_PROMPT, s.effectiveTo || month);
    if (!to) return;
    assertBillingMonth(to.trim(), '종료월');
    await endSubscription(s.id, to.trim());
    await reload();
  }, '구독 종료 완료');

  const doDelete = (s) => run(async () => {
    if (!window.confirm(`구독 #${s.id} (${s.name})을(를) 완전히 삭제합니다. 되돌릴 수 없습니다. 계속할까요?`)) return;
    await deleteSubscription(s.id);
    await reload();
  }, '구독 삭제 완료');

  const hasActive = (r) => (r.subscriptions ?? []).some((s) => isActiveForMonth(s, month));

  // ---- 플래그 필터 ----
  // 플래그는 고객사 상세에서 부여하고, 이 목록에서는 '거르는 용도'로만 쓴다.
  const [flagCodes, setFlagCodes] = useState([]);
  const [flagFilter, setFlagFilter] = useState('');
  const [flagChannelIds, setFlagChannelIds] = useState(new Set());

  useEffect(() => {
    listFlagCodes().then(({ data }) => setFlagCodes(data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!flagFilter) { setFlagChannelIds(new Set()); return; }
    let canceled = false;
    listChannelsByFlag(flagFilter)
      .then(({ data }) => { if (!canceled) setFlagChannelIds(new Set(data ?? [])); })
      .catch(() => { if (!canceled) setFlagChannelIds(new Set()); });
    return () => { canceled = true; };
  }, [flagFilter]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return rows.filter((r) => {
      if (activeOnly && r.serviceStatus !== 'ACTIVE') return false;
      if (missingOnly && hasActive(r)) return false;
      if (feeOnly && !feesByChannel.has(r.channelId)) return false;
      // 플래그 필터 — 서버에서 채널 id 집합을 받아와 교집합으로 거른다(플래그는 고객사 상세에서 부여).
      if (flagFilter && !flagChannelIds.has(r.channelId)) return false;
      if (kw) {
        const hay = `${r.channelId} ${r.channelName ?? ''} ${r.accountId ?? ''} ${r.accountOwner ?? ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, keyword, activeOnly, missingOnly, feeOnly, feesByChannel, month, flagFilter, flagChannelIds]);

  const activeCnt = rows.filter((r) => r.serviceStatus === 'ACTIVE').length;
  const missingCnt = rows.filter((r) => r.serviceStatus === 'ACTIVE' && !hasActive(r)).length;

  /**
   * 표시 순서. 기본값을 **최신순(생성번호 내림차순)** 으로 둔다 — 신규 업체 정보를 넣는 것이
   * 이 화면의 주 작업이고, 종전 오름차순에서는 새 채널이 **맨 밑**에 있어 매번 끝까지 스크롤해야 했다.
   * `channelId` 는 auto_increment 생성번호이므로 곧 생성 순서다(생성일과 같은 순서, 동시각도 안정적).
   */
  const [desc, setDesc] = useState(true);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => (desc ? b.channelId - a.channelId : a.channelId - b.channelId)),
    [filtered, desc],
  );

  /**
   * 목록 영역 높이 — 상단(제목·기준월·요약·필터)은 고정해 두고 **목록만 스크롤**시킨다.
   *
   * 왜 `h-[calc(100vh-XXXpx)]` 같은 상수를 안 쓰는가: 이 화면 위에는 Layout 의 고정 헤더(56px),
   * `.inner-layout` 마진, sysadmin 탭 네비, 그리고 **상황에 따라 나타나는 PENDING 경고 배너**가 있다.
   * 상수로 박으면 그중 하나만 바뀌어도 목록이 잘리거나 화면 밖으로 넘친다.
   * 대신 **목록 컨테이너의 실제 화면 위치를 재서** 남은 높이를 계산한다 — 위에 무엇이 몇 개 있든 맞는다.
   *
   * 다시 재는 시점: 창 크기 변경 + **상단 영역의 높이 변화**(필터가 한 줄 늘어나거나 메시지가 뜨면
   * 목록 시작 위치가 내려간다) → ResizeObserver 로 감시한다.
   */
  const headerRef = useRef(null);
  const listRef = useRef(null);
  const [listHeight, setListHeight] = useState(null);

  const recalcListHeight = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // 24px = 아래쪽 숨 여백. 너무 작아지면(작은 창) 최소 높이를 보장해 목록이 사라지지 않게 한다.
    setListHeight(Math.max(240, Math.floor(window.innerHeight - top - 24)));
  }, []);

  useEffect(() => {
    recalcListHeight();
    window.addEventListener('resize', recalcListHeight);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && headerRef.current) {
      ro = new ResizeObserver(recalcListHeight);
      ro.observe(headerRef.current);
    }
    return () => {
      window.removeEventListener('resize', recalcListHeight);
      if (ro) ro.disconnect();
    };
  }, [recalcListHeight]);

  const inputCls = 'border rounded px-1.5 py-1 text-sm w-full';

  return (
    <div className="p-6">
      {/* ===== 상단 고정 영역 — 스크롤해도 남아 있어야 하는 것들 =====
          `sticky top-0` 이 아니라 **목록만 스크롤**시키는 구조다(목록 컨테이너가 overflow-y-auto).
          sticky 로 하면 Layout 의 고정 헤더 밑으로 파고들어 겹치고, 표시 중인 건수·범례가
          화면 밖으로 밀려 "지금 무엇을 보고 있는지" 를 잃는다. */}
      <div ref={headerRef}>
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <h2 className="text-lg font-semibold">전체 고객</h2>
        <label className="text-sm text-gray-600">기준월
          {/* 자유 입력이던 것을 월 단위 풀다운으로 바꿨다(2026-09-02) — `2026-7` 같은 값이 들어가면
              조용히 빈 결과가 나왔다. 정산 관리 화면과 같은 목록을 쓴다. */}
          <select
            className="border rounded px-2 py-1 text-sm ml-1 font-semibold"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <span className="text-xs text-gray-400">(그 달 청구 구독 판정·그 달 일회성 비용 표시·추가 기본값 · 상세 화면과 공유)</span>
        <button className="px-2 py-1 text-sm border rounded" onClick={load} disabled={loading}>새로고침</button>
        {loading && <span className="text-sm text-gray-500">처리 중…</span>}
        {msg && <span className="text-sm text-blue-700">{msg}</span>}
      </div>

      {/* 요약 + 필터 */}
      <div className="flex items-center flex-wrap gap-3 mb-3 text-sm">
        <span className="text-gray-600">전체 {rows.length} · 활성 {activeCnt} · <span className={missingCnt ? 'text-red-600 font-semibold' : ''}>활성인데 구독 미등록 {missingCnt}</span></span>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          <span className="inline-block align-middle w-3 h-3 mr-1 border-2 border-black bg-white rounded-sm" />
          검정 테두리 = 활성 서비스 + {month} 활성 구독
        </span>
        <input className="border rounded px-2 py-1 text-sm" placeholder="검색(채널명/id/계정)" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        <label className="flex items-center gap-1"><input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />활성 서비스만</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />구독 미등록만</label>
        <label className="flex items-center gap-1" title="기준월에 일회성 비용(설치비·스티커 등, 취소 제외)이 걸린 채널만">
          <input type="checkbox" checked={feeOnly} onChange={(e) => setFeeOnly(e.target.checked)} />{month} 일회성 비용 있음({feesByChannel.size})
        </label>
        {feeLoadErr && <span className="text-xs text-red-600 font-semibold">{month} 일회성 비용 조회 실패 — 카드에 일회성 비용이 표시되지 않습니다 ({feeLoadErr})</span>}
        <label className="flex items-center gap-1">플래그
          <select className="border rounded px-2 py-1 text-sm" value={flagFilter} onChange={(e) => setFlagFilter(e.target.value)}>
            <option value="">전체</option>
            {flagCodes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {flagFilter && <span className="text-xs text-gray-500">‘{flagFilter}’ 채널 {flagChannelIds.size}개</span>}
        {/* 표시 순서 토글. 기본 = 최신순(신규 업체가 맨 위) */}
        <button
          className="px-2 py-1 text-sm border rounded bg-white whitespace-nowrap"
          onClick={() => setDesc((v) => !v)}
          title="생성번호(channelId) 기준 정렬을 뒤집습니다. 기본은 최신순 — 새로 만든 채널이 맨 위에 옵니다."
        >
          {desc ? '↓ 최신순' : '↑ 오래된순'}
        </button>
        <span className="text-xs text-gray-400 whitespace-nowrap ml-auto">표시 {sorted.length} / 전체 {rows.length}</span>
      </div>
      </div>

      {/* ===== 목록 — 이 영역만 스크롤된다(우측 스크롤바) =====
          🔴 `scrollbar-visible` 필수. common.css 가 `::-webkit-scrollbar { display: none }` 으로
             **앱 전역에서 스크롤바를 숨기고 있어서**, 이 클래스가 없으면 스크롤은 되지만
             스크롤바가 한 픽셀도 보이지 않는다(실측 0px). 영역 스크롤에서는 스크롤바가 없으면
             "아래에 더 있다"는 사실 자체를 알 수 없다. */}
      <div
        ref={listRef}
        className="space-y-2 overflow-y-auto pr-1 scrollbar-visible"
        style={listHeight ? { height: `${listHeight}px` } : undefined}
      >
        {sorted.map((r) => {
          const subs = r.subscriptions ?? [];
          const monthFees = feesByChannel.get(r.channelId) ?? [];
          const missing = r.serviceStatus === 'ACTIVE' && !hasActive(r);
          // 🔑 '지금 돈을 내고 서비스를 쓰는 고객' = 활성 서비스 + 그 달 활성 구독.
          //    수십 개 카드가 같은 회색으로 나열되면 이 구분이 안 보인다는 지적(2026-09-02)에 따라
          //    **테두리는 검정, 배경은 흰색(가장 밝게)** 으로 띄우고 나머지는 회색으로 물러나게 했다.
          //    테두리 두께는 세 경우 모두 border-2 로 같게 둔다 — 두께가 달라지면 행 높이가
          //    1px 씩 어긋나 목록이 미세하게 들쭉날쭉해진다.
          const live = r.serviceStatus === 'ACTIVE' && hasActive(r);
          const cardCls = live
            ? 'border-2 border-black bg-white shadow-sm'
            : missing
              ? 'border-2 border-red-300 bg-white'
              : 'border-2 border-gray-200 bg-gray-50';
          return (
            <div key={r.channelId} className={`rounded ${cardCls}`}>
              {/* 1줄: 채널 기본 정보 */}
              <div className={`flex items-center flex-wrap gap-3 px-3 py-2 border-b text-sm ${live ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="font-mono text-gray-500">#{r.channelId}</span>
                {r.logoUrl
                  ? <img src={r.logoUrl} alt="" className="w-7 h-7 rounded object-contain bg-white border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  : <span className="w-7 h-7 rounded bg-gray-200 inline-flex items-center justify-center text-[10px] text-gray-400">로고</span>}
                <span className="font-semibold">{r.channelName}</span>
                {/* 생성일 = account_channel.created_at (서버가 KST 'yyyy-MM-dd HH:mm' 로 내려준다).
                    목록에서는 날짜만 보이면 충분하고, 시각은 title 로 확인한다. */}
                <span className="text-xs text-gray-500 whitespace-nowrap" title={r.createdAt ? `채널 생성 ${r.createdAt} (KST)` : ''}>
                  생성 {r.createdAt ? r.createdAt.slice(0, 10) : '—'}
                </span>
                <span className="text-gray-500">{r.accountOwner ?? '—'}{r.accountId ? ` (${r.accountId})` : ''}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${r.serviceStatus === 'ACTIVE' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-500'}`}>{r.serviceStatus}</span>
                <Flag on={r.isUsed} label="is_used" />
                <Flag on={r.hasProfileKey} label="profile_key" />
                <Flag on={r.hasRestApiKey} label="rest_api_key" />
                <Flag on={r.hasJavascriptKey} label="javascript_key" />
                {onOpenDetail && (
                  <button
                    className="ml-auto px-2 py-0.5 text-xs border rounded bg-white hover:bg-gray-50"
                    onClick={() => onOpenDetail(r.channelId)}
                  >상세 →</button>
                )}
              </div>

              {/* 2줄: 구독 목록(구독 관리 탭과 동일) + 추가 */}
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm text-gray-500">구독 {subs.length}건</span>
                  {monthFees.length > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800">{month} 일회성 비용 {monthFees.length}건</span>
                  )}
                  {missing && <span className="text-xs text-red-600 font-semibold">⚠ 활성 서비스인데 이번달 활성 구독 없음</span>}
                  <button className="px-2 py-0.5 text-xs border rounded ml-auto" onClick={() => (feeAddingFor === r.channelId ? setFeeAddingFor(null) : openFeeAdd(r.channelId))}>
                    {feeAddingFor === r.channelId ? '닫기' : '일회성 비용 추가'}
                  </button>
                  <button className="px-2 py-0.5 text-xs border rounded" onClick={() => (addingFor === r.channelId ? setAddingFor(null) : openAdd(r.channelId))}>
                    {addingFor === r.channelId ? '닫기' : '구독 추가'}
                  </button>
                </div>

                {subs.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border">
                      <thead className="bg-gray-50">
                        <tr>
                          {['id', 'itemType', 'category', 'name', '지점수', '단가(net)', '설명', '첫 유료일', '적용 시작월', '종료월', '상태', ''].map((h) => (
                            <th key={h} className="border px-2 py-1 text-left whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {subs.map((s) => (
                          <tr key={s.id} className={s.status === 'ACTIVE' ? '' : 'bg-gray-50'}>
                            <td className="border px-1 py-1">{s.id}</td>
                            <td className="border px-1 py-1 whitespace-nowrap">{s.itemType}</td>
                            <td className="border px-1 py-1">{s.category}</td>
                            <td className="border px-1 py-1"><input className={inputCls} value={s.name} onChange={(e) => setSubField(r.channelId, s.id, 'name', e.target.value)} /></td>
                            <td className="border px-1 py-1 w-16"><input type="number" className={inputCls} value={s.branchCount} onChange={(e) => setSubField(r.channelId, s.id, 'branchCount', e.target.value)} disabled={s.category === 'ACQUISITION'} /></td>
                            <td className="border px-1 py-1 w-24"><input type="number" className={inputCls} value={s.unitPriceNet} onChange={(e) => setSubField(r.channelId, s.id, 'unitPriceNet', e.target.value)} /></td>
                            <td className="border px-1 py-1"><input className={inputCls} value={s.description || ''} onChange={(e) => setSubField(r.channelId, s.id, 'description', e.target.value)} /></td>
                            <td className="border px-1 py-1 w-32"><input type="date" min={MIN_BILLING_DATE} max={MAX_BILLING_DATE} className={inputCls} value={(s.billingStartAt || '').slice(0, 10)} onChange={(e) => setSubField(r.channelId, s.id, 'billingStartAt', e.target.value)} /></td>
                            <td className="border px-1 py-1 w-20"><input className={inputCls} value={s.effectiveFrom} onChange={(e) => setSubField(r.channelId, s.id, 'effectiveFrom', e.target.value)} /></td>
                            <td className="border px-1 py-1 w-20"><input className={inputCls} value={s.effectiveTo || ''} onChange={(e) => setSubField(r.channelId, s.id, 'effectiveTo', e.target.value)} /></td>
                            <td className="border px-1 py-1 whitespace-nowrap">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_BADGE[s.status] || ''}`}>{s.status}</span>
                              {/* 기준월에 청구되는 구독인지(서버 billableIn 과 같은 규칙) — 기준월을 바꾸면 따라 바뀐다. */}
                              {isActiveForMonth(s, month) && <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{month} 청구</span>}
                            </td>
                            <td className="border px-1 py-1 whitespace-nowrap">
                              <button className="px-2 py-1 text-xs border rounded mr-1" onClick={() => doSave(s)} disabled={loading}>저장</button>
                              {s.status === 'ACTIVE' && <button className="px-2 py-1 text-xs border rounded text-orange-600 mr-1" onClick={() => doEnd(s)} disabled={loading}>종료</button>}
                              <button className="px-2 py-1 text-xs border rounded text-red-600" onClick={() => doDelete(s)} disabled={loading}>삭제</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 기준월 일회성 비용 — 구독 표와 같은 카드 안에 둔다. 편집·취소는 고객사 상세에서(같은 데이터를 두 곳에서 고치지 않는다). */}
                {monthFees.length > 0 && (
                  <div className="overflow-x-auto mt-2">
                    <div className="text-xs text-gray-500 mb-1">
                      {month} 일회성 비용 · 합계 {won(monthFees.reduce((a, f) => a + (f.amountNet ?? 0), 0))}원(net)
                    </div>
                    <table className="min-w-full text-sm border">
                      <thead className="bg-cyan-50">
                        <tr>
                          {['id', '종류', '항목명', '금액(net)', '설명', '상태'].map((h) => (
                            <th key={h} className="border px-2 py-1 text-left whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {monthFees.map((f) => (
                          <tr key={f.id}>
                            <td className="border px-2 py-1">{f.id}</td>
                            <td className="border px-2 py-1 whitespace-nowrap">{FEE_TYPE_LABEL[f.feeType] ?? f.feeType}</td>
                            <td className="border px-2 py-1">{f.name}{f.vatExempt && <span className="ml-1 text-[10px] text-gray-400">면세</span>}</td>
                            <td className="border px-2 py-1 text-right">{won(f.amountNet)}</td>
                            <td className="border px-2 py-1 text-gray-500">{f.description ?? ''}</td>
                            <td className="border px-2 py-1 whitespace-nowrap">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${FEE_STATUS[f.status]?.cls ?? 'bg-gray-100'}`}>{FEE_STATUS[f.status]?.label ?? f.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {addingFor === r.channelId && (
                  <div className="border rounded p-3 mt-2 bg-yellow-50">
                    <div className="font-medium text-sm mb-2">구독 추가 — {r.channelName} (#{r.channelId})</div>
                    <div className="grid grid-cols-6 gap-2 items-end">
                      <label className="text-xs">상품
                        <select className={inputCls} value={draft.itemType} onChange={(e) => setDraft({ ...draft, itemType: e.target.value })}>
                          <option value="">선택…</option>
                          {catalog.map((c) => <option key={c.itemType} value={c.itemType}>{c.name} ({c.itemType}, 정가 {won(c.listPriceNet)})</option>)}
                        </select>
                      </label>
                      <label className="text-xs">적용 시작월(effectiveFrom)
                        <input className={inputCls} value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} placeholder="2026-07" />
                      </label>
                      <label className="text-xs">지점수
                        <input type="number" className={inputCls} value={draft.branchCount} onChange={(e) => setDraft({ ...draft, branchCount: e.target.value })} disabled={selectedCatalog?.category === 'ACQUISITION'} />
                      </label>
                      <label className="text-xs">단가(net, 비우면 정가 {selectedCatalog ? won(selectedCatalog.listPriceNet) : '—'})
                        <input type="number" className={inputCls} value={draft.unitPriceNet} onChange={(e) => setDraft({ ...draft, unitPriceNet: e.target.value })} placeholder={selectedCatalog ? String(selectedCatalog.listPriceNet) : ''} />
                      </label>
                      <label className="text-xs">첫 유료일(billingStartAt)
                        <input type="date" min={MIN_BILLING_DATE} max={MAX_BILLING_DATE} className={inputCls} value={draft.billingStartAt} onChange={(e) => setDraft({ ...draft, billingStartAt: e.target.value })} />
                      </label>
                      <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded" onClick={() => doCreate(r.channelId)} disabled={loading}>추가</button>
                    </div>
                    <label className="text-xs block mt-2">설명(description)
                      <input className={inputCls} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="어느 지점 / 특별가 사유 등" />
                    </label>
                    {/* 고객사 상세('구독') 화면과 **같은 문구**를 쓴다 — 설명이 화면마다 다르면 또 헷갈린다. */}
                    <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                      <b>적용 시작월</b>(YYYY-MM) = 이 구독이 정산에 잡히기 시작하는 <b>달</b>. 이 달보다 이전 달 정산에는 잡히지 않습니다.<br />
                      <b>첫 유료일</b>(YYYY-MM-DD) = 실제 과금이 시작되는 <b>날짜</b>. 그 날짜가 든 달은 <b>남은 일수만큼 일할 계산</b>되고,
                      그 이전 달은 과금 0(무료체험)입니다. <b>비우면 채널의 '과금 시작일'(고객사 상세 '과금 시작' 버튼으로 고정)이 대신 적용됩니다.</b>
                    </p>
                  </div>
                )}

                {feeAddingFor === r.channelId && (
                  <div className="border rounded p-3 mt-2 bg-cyan-50">
                    <div className="font-medium text-sm mb-2">일회성 비용 추가 — {r.channelName} (#{r.channelId})</div>
                    <div className="grid grid-cols-5 gap-2 items-end">
                      <label className="text-xs">종류
                        <select className={inputCls} value={feeDraft.feeType} onChange={(e) => pickFeeType(e.target.value)}>
                          <option value="INSTALL">설치비</option>
                          <option value="STICKER">스티커</option>
                          <option value="ETC">기타</option>
                        </select>
                      </label>
                      <label className="text-xs">항목명
                        <input className={inputCls} value={feeDraft.name} onChange={(e) => setFeeDraft({ ...feeDraft, name: e.target.value })} />
                      </label>
                      <label className="text-xs">금액(net, 부가세 별도)
                        <input type="number" className={inputCls} value={feeDraft.amountNet} onChange={(e) => setFeeDraft({ ...feeDraft, amountNet: e.target.value })} />
                      </label>
                      <label className="text-xs">청구월
                        <input className={inputCls} value={feeDraft.billingMonth} onChange={(e) => setFeeDraft({ ...feeDraft, billingMonth: e.target.value })} placeholder="2026-08" />
                      </label>
                      <button className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded" onClick={() => doCreateFee(r.channelId)} disabled={loading}>추가</button>
                    </div>
                    <label className="text-xs block mt-2">설명(description)
                      <input className={inputCls} value={feeDraft.description} onChange={(e) => setFeeDraft({ ...feeDraft, description: e.target.value })} placeholder="설치 일자 / 수량 등" />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">
                      추가하면 <b>PENDING</b> 상태로 등록되고, 해당 청구월 정산서를 생성할 때 자동으로 항목에 반영됩니다.
                      등록 내역 확인·취소는 <b>고객사 상세</b>에서 할 수 있습니다.
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {sorted.length === 0 && <div className="text-center text-gray-400 py-8 text-sm">표시할 고객이 없습니다.</div>}
      </div>
    </div>
  );
}
